import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Typography, Tag, Space, Alert, Spin, Empty, Progress, Input, List, Popover,
} from 'antd';
import { HelpCircle } from 'lucide-react';
import MerchantDetailModal from '../components/MerchantDetailModal';
import { motion } from 'framer-motion';
import CountUp from '../components/CountUp';
import {
  MoonStar, TrendingUp, OctagonAlert, ArrowUpRight, ArrowDownRight, Minus,
  CalendarDays, RefreshCw, ChevronRight,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie,
} from 'recharts';
import { api, fmt } from '../api';

const { Title, Text } = Typography;

const SHOW_REALTIME = true;

type SalesBlock = {
  yesterday: number; today: number;
  today_change_pct: number | null;
  last_month: number; this_month: number;
  this_month_change_pct: number | null;
  yesterday_cancel: number;
  today_cancel: number;
  last_month_cancel: number;
  this_month_cancel: number;
};

type Summary = {
  asof: { today: string; yesterday: string; month_start: string; last_month_start: string; last_month_end: string };
  discount_sales: SalesBlock;
  realtime_sales: SalesBlock;
  repay_progress: {
    merchant_count: number; purchase_total: number; deposit_total: number; expected_profit: number;
    daily_deduct_total: number; principal_repaid_today: number;
    today_shortfall: number; yesterday_shortfall: number; shortfall_change_pct: number | null;
    prev_shortfall_total: number;
  };
  bad_contracts: { merchant_count: number; purchase_total: number; deposit_total: number };
  dormant_count: number;
};

const wonShort = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1e8) return `${(n / 1e8).toFixed(1)}억`;
  if (abs >= 1e4) return `${(n / 1e4).toFixed(0)}만`;
  return n.toLocaleString('ko-KR');
};

const BRAND = '#1f7c46';
const BRAND_LIGHT = '#82cb98';
const NEUTRAL = '#9ba39e';
const WARN = '#c83a3a';
const AMBER = '#d97706';

function RangeStat({ label, value, sub, highlight = false }: { label: string; value: number; sub?: string; highlight?: boolean }) {
  return (
    <div style={{
      padding: '12px 14px',
      background: highlight ? '#f0fdf4' : '#fafafa',
      border: highlight ? '1px solid #bbf7d0' : '1px solid var(--border-soft)',
      borderRadius: 8,
    }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: highlight ? 20 : 18, fontWeight: 700, color: highlight ? 'var(--leaf-800)' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
        {fmt.won(value)}<span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 2 }}>원</span>
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function DeltaTag({ value }: { value: number | null }) {
  if (value === null) return null;
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const color = value > 0 ? 'green' : value < 0 ? 'red' : 'default';
  return (
    <Tag color={color} bordered={false} style={{ marginInlineEnd: 0, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <Icon size={11} strokeWidth={2.6} />
      {value > 0 ? '+' : ''}{value.toFixed(1)}%
    </Tag>
  );
}

/**
 * SalesMetricCard
 * 카드 안에 [라벨][승인/취소/합계 3줄][델타+미니바].
 * 승인 = value (양수, PG 보고서의 매출 합)
 * 취소 = cancel (음수)
 * 합계 = value + cancel (= 순매출)
 */
function SalesMetricCard({
  label, value, cancel, period, delta, allValues, currentIdx, highlight = false,
}: {
  label: string;
  value: number;            // 승인 합
  cancel: number;           // 취소 합 (음수)
  period?: string;          // 카드 상단 부가 정보 (예: '2026-05-19' 또는 '전월 대비')
  delta?: number | null;
  allValues: number[];      // 같은 카테고리 내 모든 시점 합계(승인+취소)
  currentIdx: number;       // 이 카드가 그 중 몇 번째
  highlight?: boolean;
}) {
  const total = value + cancel;
  const max = Math.max(...allValues.map(Math.abs), 1);
  const bars = allValues.map((v, i) => ({
    name: ['전일', '오늘', '전월', '당월'][i],
    value: Math.max(0, v),
    pct: Math.round((Math.max(0, v) / max) * 100),
    isCurrent: i === currentIdx,
  }));
  const lineLabelStyle: React.CSSProperties = {
    fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '.2px',
    flex: '0 0 auto', minWidth: 32,
  };
  const lineValueStyle: React.CSSProperties = {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 700, letterSpacing: '-0.3px',
    textAlign: 'right', flex: 1,
  };
  return (
    <Card
      styles={{ body: { padding: '16px 18px' } }}
      style={{
        height: '100%',
        ...(highlight ? { borderColor: BRAND_LIGHT } : {}),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <Text style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</Text>
        {period && <Text type="secondary" style={{ fontSize: 10.5 }}>{period}</Text>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
        {/* 합계 (PG 보고서 "이달 매출"과 같은 정의 — 가장 강조) */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}
             title="승인 합 + 취소 합. PG 보고서의 정합 매출과 같은 정의.">
          <span style={{ ...lineLabelStyle, color: 'var(--text)' }}>합계</span>
          <span style={{ ...lineValueStyle, fontSize: highlight ? 22 : 19, color: BRAND }}>
            <CountUp to={total} duration={0.7} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 2 }}>원</span>
          </span>
        </div>
        {/* 승인 (sub) */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8,
                     borderTop: '1px solid var(--border-soft)', paddingTop: 6 }}>
          <span style={lineLabelStyle}>승인</span>
          <span style={{ ...lineValueStyle, fontSize: 13, color: 'var(--text-muted)' }}>
            {fmt.won(value)}
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 2 }}>원</span>
          </span>
        </div>
        {/* 취소 (sub) */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={lineLabelStyle}>취소</span>
          <span style={{ ...lineValueStyle, fontSize: 13, color: cancel < 0 ? WARN : 'var(--text-faint)' }}>
            {fmt.won(cancel)}
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 2 }}>원</span>
          </span>
        </div>
      </div>

      {delta !== undefined && (
        <div style={{ marginBottom: 6 }}>
          <DeltaTag value={delta ?? null} />
        </div>
      )}

      {/* 미니 막대 — 4개 시점 상대 크기 비교 (합계 기준) */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 30 }}>
        {bars.map((b, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
               title={`${b.name} ${fmt.won(b.value)}`}>
            <div style={{
              width: '100%',
              height: `${Math.max(3, b.pct * 0.24)}px`,
              background: b.isCurrent ? BRAND : '#dde3df',
              borderRadius: 2,
              transition: 'background .15s',
            }} />
            <span style={{
              fontSize: 9, color: b.isCurrent ? BRAND : 'var(--text-faint)',
              fontWeight: b.isCurrent ? 700 : 500,
            }}>{b.name}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function HeroKpiCard({
  label, value, unit, iconClass, Icon, tone, onClick, sub,
}: {
  label: string; value: number; unit: string;
  iconClass: string; Icon: LucideIcon;
  tone?: 'alert' | 'warn' | 'ok' | 'inactive';
  onClick?: () => void;
  sub: React.ReactNode;
}) {
  // 상태별 색상 팔레트 — 정상(초록) / 확인 필요(주황) / 처리 필요·오류(빨강) / 계약 종료·비활성(회색)
  const valColor =
    tone === 'warn' ? WARN :
    tone === 'alert' ? AMBER :
    tone === 'ok' ? BRAND :
    tone === 'inactive' ? '#64748b' :
    'var(--text)';
  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      styles={{ body: { padding: '20px 22px' } }}
      style={{ cursor: onClick ? 'pointer' : 'default', height: '100%' }}
    >
      <Row align="middle" gutter={16}>
        <Col flex="48px">
          <div className={`hero-icon-box ${iconClass}`}><Icon size={22} /></div>
        </Col>
        <Col flex="auto">
          <Text style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</Text>
          <div style={{
            fontSize: 28, fontWeight: 700, color: valColor,
            letterSpacing: '-0.7px', lineHeight: 1.15, marginTop: 4,
            fontVariantNumeric: 'tabular-nums',
          }}>
            <CountUp to={value} duration={0.7} />
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 4 }}>{unit}</span>
          </div>
          <Text type="secondary" style={{ fontSize: 11.5 }}>{sub}</Text>
        </Col>
      </Row>
    </Card>
  );
}

// 금액 지표용 KPI 카드 — 큰 숫자에 원화 포맷. HeroKpiCard와 스타일 통일.
function MetricKpiCard({
  label, value, unit, iconClass, Icon, tone, onClick, sub,
}: {
  label: string; value: number; unit: string;
  iconClass: string; Icon: LucideIcon;
  tone?: 'alert' | 'warn' | 'ok' | 'inactive';
  onClick?: () => void;
  sub: React.ReactNode;
}) {
  const valColor =
    tone === 'warn' ? WARN :
    tone === 'alert' ? AMBER :
    tone === 'ok' ? BRAND :
    tone === 'inactive' ? '#94a3b8' :
    'var(--text)';
  const isCurrency = unit === '원';
  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      styles={{ body: { padding: '16px 18px' } }}
      style={{ cursor: onClick ? 'pointer' : 'default', height: '100%' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div className={`hero-icon-box ${iconClass}`} style={{ width: 30, height: 30 }}>
          <Icon size={16} />
        </div>
        <Text style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</Text>
      </div>
      <div style={{
        fontSize: isCurrency ? 20 : 24, fontWeight: 700, color: valColor,
        letterSpacing: '-0.5px', lineHeight: 1.15,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {isCurrency ? fmt.won(value) : <CountUp to={value} duration={0.7} />}
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 4 }}>{unit}</span>
      </div>
      <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>{sub}</Text>
    </Card>
  );
}

// 통합검색용 가맹점 목록 캐시 — 대시보드 진입 시 1회 로드.
type MerchantHit = {
  mid: string; name: string;
  ceo_name: string | null; business_no: string | null;
  category: string; active: number;
};

export default function Dashboard() {
  const [s, setS] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // 통합검색 상태
  const [searchQ, setSearchQ] = useState('');
  const [merchants, setMerchants] = useState<MerchantHit[]>([]);
  const [detailMid, setDetailMid] = useState<string | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // ===== 산정기간 카스텀 조회 =====
  // 기본값: 이번 달 1일 ~ 오늘 (KST). 사용자가 자유롭게 변경 가능.
  type RangeResp = {
    from: string; to: string;
    discount: { paid: number; cancel: number; net: number };
    realtime: { paid: number; cancel: number; net: number };
    total:    { paid: number; cancel: number; net: number };
  };
  const todayKst = () => {
    const d = new Date(Date.now() + 9 * 3600_000);
    return d.toISOString().slice(0, 10);
  };
  const monthStartKst = () => todayKst().slice(0, 7) + '-01';
  const [rangeFrom, setRangeFrom] = useState(monthStartKst());
  const [rangeTo, setRangeTo] = useState(todayKst());
  const [rangeData, setRangeData] = useState<RangeResp | null>(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeErr, setRangeErr] = useState<string | null>(null);

  const loadRange = async (from: string, to: string) => {
    setRangeLoading(true); setRangeErr(null);
    try {
      const r = await api<RangeResp>('GET', `/dashboard/range?from=${from}&to=${to}`);
      setRangeData(r);
    } catch (e: any) {
      setRangeErr(e?.message ?? '기간 조회 실패');
      setRangeData(null);
    } finally { setRangeLoading(false); }
  };
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api<Summary>('GET', '/dashboard/summary');
      setS(r);
    } catch (e: any) {
      setErr(e?.message ?? '조회 실패');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadRange(rangeFrom, rangeTo); /* 첫 로드만 자동 */ /* eslint-disable-next-line */ }, []);
  // 통합검색용 가맹점 목록 프리페치 — 재사용 위해 진입 시 1회.
  useEffect(() => {
    (async () => {
      try {
        const r = await api<{ data: MerchantHit[] }>('GET', '/merchants');
        setMerchants(r.data ?? []);
      } catch {}
    })();
  }, []);
  // 검색 결과 밖 클릭 시 드롭다운 닫기
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const searchHits: MerchantHit[] = useMemo(() => {
    const s = searchQ.trim().toLowerCase();
    if (!s) return [];
    const digits = s.replace(/\D/g, '');
    return merchants.filter(m => {
      const bnoDigits = (m.business_no ?? '').replace(/\D/g, '');
      return m.mid.toLowerCase().includes(s)
          || m.name.toLowerCase().includes(s)
          || (m.ceo_name?.toLowerCase().includes(s) ?? false)
          || (m.business_no?.toLowerCase().includes(s) ?? false)
          || (digits.length >= 3 && bnoDigits.includes(digits));
    }).slice(0, 20);
  }, [searchQ, merchants]);

  if (loading) {
    return (
      <div>
        <Title level={2} style={{ margin: 0 }}>대시보드</Title>
        <div style={{ padding: '60px 0', textAlign: 'center' }}><Spin size="large" /></div>
      </div>
    );
  }
  if (err || !s) {
    return (
      <div>
        <Title level={2} style={{ margin: 0 }}>대시보드</Title>
        <Alert type="error" message={err ?? '데이터 없음'} showIcon style={{ marginTop: 16 }} />
      </div>
    );
  }

  // ===== 파생값 =====
  const ds = s.discount_sales;
  const rs = s.realtime_sales;
  const rp = s.repay_progress;

  // 미니바·차트·도넛은 모두 합계(승인+취소) 기준으로 시각화 — 카드의 "합계" 라인과 일치
  const discTotals  = [
    ds.yesterday + ds.yesterday_cancel,
    ds.today + ds.today_cancel,
    ds.last_month + ds.last_month_cancel,
    ds.this_month + ds.this_month_cancel,
  ];
  const rtTotals    = [
    rs.yesterday + rs.yesterday_cancel,
    rs.today + rs.today_cancel,
    rs.last_month + rs.last_month_cancel,
    rs.this_month + rs.this_month_cancel,
  ];

  // 회수율 = 입금액 / 매입액
  const repayRate = rp.purchase_total > 0
    ? Math.round((rp.deposit_total / rp.purchase_total) * 100)
    : 0;

  // 당일 회수 효율 = 원금상환 / 일차감
  const todayEfficiency = rp.daily_deduct_total > 0
    ? Math.round((rp.principal_repaid_today / rp.daily_deduct_total) * 100)
    : 0;

  // 오늘 매출 구성 (할인매매 vs 실시간) — 합계 기준 (승인+취소). SHOW_REALTIME=false면 할인매매만.
  const discTodayTotal = Math.max(0, discTotals[1]);
  const rtTodayTotal   = Math.max(0, rtTotals[1]);
  const todayTotal     = SHOW_REALTIME ? (discTodayTotal + rtTodayTotal) : discTodayTotal;
  const todayComp = SHOW_REALTIME
    ? [
        { name: '할인매매', value: discTodayTotal, color: BRAND },
        { name: '실시간',   value: rtTodayTotal,   color: NEUTRAL },
      ]
    : [
        { name: '할인매매', value: discTodayTotal, color: BRAND },
      ];

  return (
    <div>
      {/* 페이지 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>대시보드</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            오늘({s.asof.today}) 기준 매출·회수·계약 현황 한눈에 보기
          </Text>
        </div>
        <Space>
          <div ref={searchWrapRef} style={{ position: 'relative', width: 320 }}>
            <Input
              placeholder="가맹점 통합검색 (가맹점명 · 대표자명 · 사업자등록번호 · MID)"
              value={searchQ}
              onChange={e => { setSearchQ(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              allowClear
              size="middle"
              style={{ width: '100%' }}
            />
            {searchOpen && searchQ.trim() && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 60,
                maxHeight: 320, overflow: 'auto',
              }}>
                {searchHits.length === 0 ? (
                  <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 12 }}>
                    일치하는 가맹점이 없습니다.
                  </div>
                ) : (
                  <List size="small" dataSource={searchHits}
                        renderItem={(m: MerchantHit) => (
                          <List.Item
                            style={{ cursor: 'pointer', padding: '8px 12px' }}
                            onClick={() => { setDetailMid(m.mid); setSearchOpen(false); }}
                          >
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                <b style={{ fontSize: 13 }}>{m.name}</b>
                                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{m.mid}</span>
                                {m.active === 0 && <Tag color="default" style={{ marginLeft: 'auto' }}>비활성</Tag>}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                {m.ceo_name ? `대표 ${m.ceo_name}` : ''}
                                {m.ceo_name && m.business_no ? ' · ' : ''}
                                {m.business_no ? `사업자 ${m.business_no}` : ''}
                              </div>
                            </div>
                          </List.Item>
                        )} />
                )}
              </div>
            )}
          </div>
          <Tag icon={<CalendarDays size={12} />} bordered={false} color="default" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            기준일 {s.asof.today}
          </Tag>
          <a onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <RefreshCw size={13} /> 새로고침
          </a>
        </Space>
      </div>

      {/* ===== 산정기간 카스텀 조회 ===== */}
      <Card
        styles={{ body: { padding: '14px 18px' } }}
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text strong style={{ fontSize: 13 }}>산정기간</Text>
          <input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)}
                 style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6 }} />
          <span style={{ color: 'var(--text-muted)' }}>~</span>
          <input type="date" value={rangeTo} onChange={e => setRangeTo(e.target.value)}
                 style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6 }} />
          <button
            className="btn"
            onClick={() => loadRange(rangeFrom, rangeTo)}
            disabled={rangeLoading}
            style={{ padding: '6px 14px' }}
          >{rangeLoading ? '조회 중…' : '조회'}</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {[
              { label: '이번 달', f: monthStartKst(), t: todayKst() },
              { label: '어제 1일', f: (() => { const d = new Date(Date.now() + 9*3600_000); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0,10); })(), t: (() => { const d = new Date(Date.now() + 9*3600_000); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0,10); })() },
              { label: '오늘', f: todayKst(), t: todayKst() },
              { label: '최근 7일', f: (() => { const d = new Date(Date.now() + 9*3600_000); d.setUTCDate(d.getUTCDate() - 6); return d.toISOString().slice(0,10); })(), t: todayKst() },
            ].map(p => (
              <button key={p.label} className="btn secondary sm"
                      style={{ fontSize: 11, padding: '4px 10px' }}
                      onClick={() => { setRangeFrom(p.f); setRangeTo(p.t); loadRange(p.f, p.t); }}
              >{p.label}</button>
            ))}
          </div>
        </div>
        {rangeErr && <Alert type="error" message={rangeErr} showIcon style={{ marginTop: 10 }} />}
        {rangeData && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 14 }}>
            <RangeStat label="할인매매 정합 매출"
                       value={rangeData.discount.net}
                       sub={`승인 ${rangeData.discount.paid.toLocaleString('ko-KR')} · 취소 ${rangeData.discount.cancel.toLocaleString('ko-KR')}`} />
            {SHOW_REALTIME && (
              <RangeStat label="실시간 정합 매출"
                         value={rangeData.realtime.net}
                         sub={`승인 ${rangeData.realtime.paid.toLocaleString('ko-KR')} · 취소 ${rangeData.realtime.cancel.toLocaleString('ko-KR')}`} />
            )}
            <RangeStat label="전체 정합 매출" highlight
                       value={rangeData.total.net}
                       sub={`승인 ${rangeData.total.paid.toLocaleString('ko-KR')} · 취소 ${rangeData.total.cancel.toLocaleString('ko-KR')}`} />
          </div>
        )}
      </Card>

      {/* ===== 핵심 지표 (필수 우선 배치) — 각 카드 클릭 시 관련 화면·필터로 즉시 이동 ===== */}
      <Title level={4} style={{ margin: '4px 0 12px', fontSize: 15, fontWeight: 700 }}>
        핵심 지표
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 10 }}>
          운영 중 자주 확인하는 지표 — 카드 클릭 시 대상 화면으로 이동
        </Text>
      </Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <MetricKpiCard
            label="전일까지 미차감"
            value={rp.prev_shortfall_total}
            unit="원"
            Icon={TrendingUp}
            iconClass={rp.prev_shortfall_total > 0 ? 'alert' : 'ok'}
            tone={rp.prev_shortfall_total > 0 ? 'alert' : 'ok'}
            onClick={() => navigate('/settlement/discount')}
            sub="할인매매 이체정산에서 확인"
          />
        </Col>
        <Col xs={12} md={6}>
          <MetricKpiCard
            label="오늘 원금 반환"
            value={rp.principal_repaid_today}
            unit="원"
            Icon={TrendingUp}
            iconClass={rp.principal_repaid_today > 0 ? 'brand' : 'inactive'}
            tone={rp.principal_repaid_today > 0 ? 'ok' : 'inactive'}
            onClick={() => navigate('/settlement/discount')}
            sub={`목표 ${fmt.won(rp.daily_deduct_total)} · 회수 효율 ${todayEfficiency}%`}
          />
        </Col>
        <Col xs={12} md={6}>
          <MetricKpiCard
            label="당일 미차감"
            value={rp.today_shortfall}
            unit="원"
            Icon={OctagonAlert}
            iconClass={rp.today_shortfall > 0 ? 'warn' : 'ok'}
            tone={rp.today_shortfall > 0 ? 'warn' : 'ok'}
            onClick={() => navigate('/settlement/discount')}
            sub={rp.shortfall_change_pct != null
              ? `전일 대비 ${rp.shortfall_change_pct > 0 ? '+' : ''}${rp.shortfall_change_pct}%`
              : '전일 대비 -'}
          />
        </Col>
        <Col xs={12} md={6}>
          <MetricKpiCard
            label="확인 필요 건수"
            value={s.dormant_count + s.bad_contracts.merchant_count}
            unit="건"
            Icon={OctagonAlert}
            iconClass={(s.dormant_count + s.bad_contracts.merchant_count) > 0 ? 'alert' : 'ok'}
            tone={(s.dormant_count + s.bad_contracts.merchant_count) > 0 ? 'alert' : 'ok'}
            onClick={() => navigate('/dormant')}
            sub={`3일 무매출 ${s.dormant_count} · 미반환 ${s.bad_contracts.merchant_count}`}
          />
        </Col>
      </Row>

      {/* ===== 계약 현황 알림 (기존 Hero) ===== */}
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <HeroKpiCard
            label="3일 무매출 가맹점"
            value={s.dormant_count}
            unit="개"
            Icon={MoonStar}
            iconClass={s.dormant_count > 0 ? 'alert' : 'ok'}
            tone={s.dormant_count > 0 ? 'alert' : 'ok'}
            onClick={() => navigate('/dormant')}
            sub={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>목록 보기 <ChevronRight size={12} /></span>}
          />
        </Col>
        <Col xs={24} md={8}>
          <HeroKpiCard
            label="진행 중 가맹점 (반환)"
            value={rp.merchant_count}
            unit="개"
            Icon={TrendingUp}
            iconClass="brand"
            tone="ok"
            onClick={() => navigate('/merchants')}
            sub="할인매매 계약 진행 — 가맹점 관리로 이동"
          />
        </Col>
        <Col xs={24} md={8}>
          <HeroKpiCard
            label="악성 (미반환)"
            value={s.bad_contracts.merchant_count}
            unit="개"
            Icon={OctagonAlert}
            iconClass={s.bad_contracts.merchant_count > 0 ? 'warn' : ''}
            tone={s.bad_contracts.merchant_count > 0 ? 'warn' : 'ok'}
            onClick={() => navigate('/merchants')}
            sub="계약 종료 후 미반환 — 가맹점 관리로 이동"
          />
        </Col>
      </Row>

      {/* ===== 카드매출 통합 표 — 4개 시점 × 할인매매/실시간/합계 (요청서: 중복 카드 정리) ===== */}
      <Title level={4} style={{ margin: '8px 0 14px', fontSize: 16, fontWeight: 700 }}>
        카드매출 요약
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 10 }}>
          시점별 승인 · 취소 · 합계 (합계 = 승인 + 취소)
        </Text>
      </Title>
      {(() => {
        // 시점 4개 정의 — 각 시점의 [할인매매, 실시간] 승인/취소/합계.
        const points = [
          { label: '전일', period: s.asof.yesterday, disc: { paid: ds.yesterday, cancel: ds.yesterday_cancel }, rt: { paid: rs.yesterday, cancel: rs.yesterday_cancel } },
          { label: '오늘', period: '오늘', disc: { paid: ds.today, cancel: ds.today_cancel }, rt: { paid: rs.today, cancel: rs.today_cancel }, delta: ds.today_change_pct, deltaRt: rs.today_change_pct },
          { label: '전월 누적', period: `${s.asof.last_month_start} ~ ${s.asof.last_month_end}`, disc: { paid: ds.last_month, cancel: ds.last_month_cancel }, rt: { paid: rs.last_month, cancel: rs.last_month_cancel } },
          { label: '당월 누적', period: `${s.asof.month_start} ~ ${s.asof.today}`, disc: { paid: ds.this_month, cancel: ds.this_month_cancel }, rt: { paid: rs.this_month, cancel: rs.this_month_cancel }, delta: ds.this_month_change_pct, deltaRt: rs.this_month_change_pct },
        ];
        const cellCol = (paid: number, cancel: number) => {
          const total = paid + cancel;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: BRAND, fontVariantNumeric: 'tabular-nums' }}>
                {fmt.won(total)}<span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 2 }}>원</span>
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                승인 {fmt.won(paid)} · 취소 {cancel < 0 ? fmt.won(cancel) : fmt.won(cancel)}
              </span>
            </div>
          );
        };
        return (
          <Card styles={{ body: { padding: 0 } }} style={{ marginBottom: 22, overflow: 'hidden' }}>
            <div className="table-wrap" style={{ border: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th className="nowrap" style={{ background: '#f8fafc' }}>시점</th>
                    <th className="nowrap" style={{ background: '#f8fafc' }}>기준</th>
                    <th className="num nowrap" style={{ background: '#f8fafc' }}>할인매매</th>
                    {SHOW_REALTIME && <th className="num nowrap" style={{ background: '#f8fafc' }}>실시간</th>}
                    <th className="num nowrap" style={{ background: '#f0fdf4' }}>전체 합계</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((p, i) => {
                    const highlight = i === 1 || i === 3;
                    const discTotal = p.disc.paid + p.disc.cancel;
                    const rtTotal = p.rt.paid + p.rt.cancel;
                    const total = discTotal + (SHOW_REALTIME ? rtTotal : 0);
                    return (
                      <tr key={p.label} style={highlight ? { background: '#f8fafc' } : undefined}>
                        <td className="nowrap" style={{ fontWeight: 700, fontSize: 13 }}>
                          {p.label}
                          {p.delta != null && (
                            <div style={{ marginTop: 3 }}>
                              <DeltaTag value={p.delta} />
                            </div>
                          )}
                        </td>
                        <td className="nowrap" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {p.period}
                        </td>
                        <td className="num nowrap">{cellCol(p.disc.paid, p.disc.cancel)}</td>
                        {SHOW_REALTIME && <td className="num nowrap">{cellCol(p.rt.paid, p.rt.cancel)}</td>}
                        <td className="num nowrap" style={{ background: '#f0fdf4' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--leaf-800)', fontVariantNumeric: 'tabular-nums' }}>
                              {fmt.won(total)}<span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 2 }}>원</span>
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* SalesMetricCard 상세 뷰 — 기본 접힘. 시점별 미니바·개별 카드가 필요할 때만 펼치기. */}
      <details style={{ marginBottom: 22 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', padding: '6px 0' }}>
          시점별 상세 카드 (미니바·전월 대비 등 자세히 보기)
        </summary>
        <div style={{ marginTop: 12 }}>
          <Title level={5} style={{ margin: '4px 0 10px', fontSize: 14, fontWeight: 700 }}>할인매매 카드매출</Title>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <SalesMetricCard label="전일 매출" value={ds.yesterday} cancel={ds.yesterday_cancel}
                               period={s.asof.yesterday} allValues={discTotals} currentIdx={0} />
            </Col>
            <Col xs={12} md={6}>
              <SalesMetricCard label="오늘 매출" value={ds.today} cancel={ds.today_cancel}
                               period="전일 대비" delta={ds.today_change_pct}
                               allValues={discTotals} currentIdx={1} highlight />
            </Col>
            <Col xs={12} md={6}>
              <SalesMetricCard label="전월 누적" value={ds.last_month} cancel={ds.last_month_cancel}
                               period={`${s.asof.last_month_start} ~ ${s.asof.last_month_end}`}
                               allValues={discTotals} currentIdx={2} />
            </Col>
            <Col xs={12} md={6}>
              <SalesMetricCard label="당월 누적" value={ds.this_month} cancel={ds.this_month_cancel}
                               period="전월 대비" delta={ds.this_month_change_pct}
                               allValues={discTotals} currentIdx={3} highlight />
            </Col>
          </Row>
          {SHOW_REALTIME && (
            <>
              <Title level={5} style={{ margin: '4px 0 10px', fontSize: 14, fontWeight: 700 }}>실시간 카드매출</Title>
              <Row gutter={[12, 12]}>
                <Col xs={12} md={6}>
                  <SalesMetricCard label="전일 매출" value={rs.yesterday} cancel={rs.yesterday_cancel}
                                   period={s.asof.yesterday} allValues={rtTotals} currentIdx={0} />
                </Col>
                <Col xs={12} md={6}>
                  <SalesMetricCard label="오늘 매출" value={rs.today} cancel={rs.today_cancel}
                                   period="전일 대비" delta={rs.today_change_pct}
                                   allValues={rtTotals} currentIdx={1} highlight />
                </Col>
                <Col xs={12} md={6}>
                  <SalesMetricCard label="전월 누적" value={rs.last_month} cancel={rs.last_month_cancel}
                                   period={`${s.asof.last_month_start} ~ ${s.asof.last_month_end}`}
                                   allValues={rtTotals} currentIdx={2} />
                </Col>
                <Col xs={12} md={6}>
                  <SalesMetricCard label="당월 누적" value={rs.this_month} cancel={rs.this_month_cancel}
                                   period="전월 대비" delta={rs.this_month_change_pct}
                                   allValues={rtTotals} currentIdx={3} highlight />
                </Col>
              </Row>
            </>
          )}
        </div>
      </details>

      {/* ===== 차트 — 비교/구성 ===== */}
      <Title level={4} style={{ margin: '8px 0 14px', fontSize: 16, fontWeight: 700 }}>
        한눈에 보기
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 10 }}>
          매출 비교 · 오늘 매출 구성
        </Text>
      </Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        {/* 1. 매출 비교 막대 */}
        <Col xs={24} lg={12}>
          <Card
            title="카드매출 비교"
            extra={<Text type="secondary" style={{ fontSize: 11.5 }}>4개 시점 × 종류별</Text>}
            styles={{ body: { paddingTop: 12 } }}
          >
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: '전일', 할인매매: discTotals[0], 실시간: rtTotals[0] },
                    { name: '오늘', 할인매매: discTotals[1], 실시간: rtTotals[1] },
                    { name: '전월', 할인매매: discTotals[2], 실시간: rtTotals[2] },
                    { name: '당월', 할인매매: discTotals[3], 실시간: rtTotals[3] },
                  ]}
                  margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={wonShort} axisLine={false} tickLine={false} width={52} />
                  <Tooltip formatter={(v: any) => fmt.won(Number(v))} cursor={{ fill: 'rgba(0,0,0,.03)' }} />
                  <Bar dataKey="할인매매" fill={BRAND}    radius={[4, 4, 0, 0]} maxBarSize={28} />
                  {SHOW_REALTIME && <Bar dataKey="실시간"   fill={NEUTRAL}  radius={[4, 4, 0, 0]} maxBarSize={28} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Space size={16} style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: BRAND, marginRight: 6 }} />할인매매</span>
              {SHOW_REALTIME && <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: NEUTRAL, marginRight: 6 }} />실시간</span>}
            </Space>
          </Card>
        </Col>

        {/* 2. 오늘 매출 구성 도넛 */}
        <Col xs={24} lg={12}>
          <Card
            title="오늘 매출 구성"
            extra={<Text type="secondary" style={{ fontSize: 11.5 }}>총 {wonShort(todayTotal)}원</Text>}
            styles={{ body: { paddingTop: 12 } }}
          >
            <div style={{ width: '100%', height: 240, position: 'relative' }}>
              {todayTotal === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="오늘 매출 0" style={{ paddingTop: 60 }} />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={todayComp} dataKey="value" innerRadius={70} outerRadius={102} paddingAngle={2} stroke="none">
                        {todayComp.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip formatter={(v: any) => fmt.won(Number(v))} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none', flexDirection: 'column',
                  }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '.4px', textTransform: 'uppercase' }}>할인매매 비중</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.6px', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                      {todayTotal > 0 ? Math.round((discTodayTotal / todayTotal) * 100) : 0}%
                    </div>
                  </div>
                </>
              )}
            </div>
            <Space size={16} style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: BRAND, marginRight: 6 }} />할인매매 <b style={{ color: 'var(--text)' }}>{wonShort(discTodayTotal)}원</b></span>
              {SHOW_REALTIME && <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: NEUTRAL, marginRight: 6 }} />실시간 <b style={{ color: 'var(--text)' }}>{wonShort(rtTodayTotal)}원</b></span>}
            </Space>
          </Card>
        </Col>

      </Row>

      {/* ===== 진행 중 가맹점 — Progress Bar 형태 ===== */}
      <Title level={4} style={{ margin: '8px 0 14px', fontSize: 16, fontWeight: 700 }}>
        할인매매 계약 진행 현황
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 10 }}>
          진척률 시각화 · 회수/지급 흐름
        </Text>
      </Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        {/* 매입/입금/수익 progress */}
        <Col xs={24} lg={12}>
          <Card title="자금 흐름" styles={{ body: { padding: 22 } }}>
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>매입액 (받아야 할 돈)</Text>
                  <Text style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmt.won(rp.purchase_total)}</Text>
                </div>
                <Progress percent={100} strokeColor="#cfd5d2" showInfo={false} size={['100%', 10]} />
                <Text type="secondary" style={{ fontSize: 11 }}>100% — 기준</Text>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>입금액 (실 지급)</Text>
                  <Text style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmt.won(rp.deposit_total)}</Text>
                </div>
                <Progress percent={repayRate} strokeColor={BRAND} showInfo={false} size={['100%', 10]} />
                <Text type="secondary" style={{ fontSize: 11 }}>매입 대비 {repayRate}%</Text>
              </div>

            </Space>
          </Card>
        </Col>

        {/* 회수 효율 + 미차감 */}
        <Col xs={24} lg={12}>
          <Card title="당일 회수 효율 · 미차감" styles={{ body: { padding: 22 } }}>
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>당일 원금 반환 / 일차감 목표</Text>
                  <Text style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmt.won(rp.principal_repaid_today)} / {fmt.won(rp.daily_deduct_total)}
                  </Text>
                </div>
                <Progress
                  percent={Math.min(100, todayEfficiency)}
                  strokeColor={todayEfficiency >= 80 ? BRAND : todayEfficiency >= 40 ? AMBER : WARN}
                  showInfo={false} size={['100%', 12]}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>회수 효율 {todayEfficiency}%</Text>
              </div>

              <Row gutter={12}>
                <Col span={12}>
                  <Card size="small" styles={{ body: { padding: '12px 14px' } }}>
                    <Statistic
                      title="당일 미차감"
                      value={rp.today_shortfall}
                      formatter={(v) => fmt.won(Number(v))}
                      valueStyle={{ fontSize: 18, fontWeight: 700, color: rp.today_shortfall > 0 ? WARN : 'var(--text)' }}
                      suffix={<DeltaTag value={rp.shortfall_change_pct} />}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" styles={{ body: { padding: '12px 14px' } }}>
                    <Statistic
                      title="전일까지 미차감"
                      value={rp.prev_shortfall_total}
                      formatter={(v) => fmt.won(Number(v))}
                      valueStyle={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}
                    />
                  </Card>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Card size="small" styles={{ body: { padding: '12px 14px' } }}>
                    <Statistic
                      title="계약 가맹점 수"
                      value={rp.merchant_count}
                      suffix="개"
                      valueStyle={{ fontSize: 18, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" styles={{ body: { padding: '12px 14px' } }}>
                    <Statistic
                      title="일차감액 합계"
                      value={rp.daily_deduct_total}
                      formatter={(v) => fmt.won(Number(v))}
                      valueStyle={{ fontSize: 18, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ===== 분류 안내 — 팝오버로 축약 (기존 3개 카드 자리 절약) ===== */}
      <div style={{ margin: '4px 0 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Popover
          placement="topLeft"
          trigger="click"
          content={
            <div style={{ maxWidth: 360, fontSize: 12.5, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Tag color="gold" bordered={false} style={{ margin: 0, fontWeight: 600, flexShrink: 0 }}>반환</Tag>
                  <span>반환이 진행 중인 가맹점. 일차감으로 매입액을 회수 중입니다.</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Tag color="blue" bordered={false} style={{ margin: 0, fontWeight: 600, flexShrink: 0 }}>일반</Tag>
                  <span>계약이 끝났지만 단말기를 계속 사용하는 가맹점.</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Tag color="red" bordered={false} style={{ margin: 0, fontWeight: 600, flexShrink: 0 }}>악성</Tag>
                  <span>계약 기간이 종료되었음에도 반환하지 못한 가맹점. 약정 만기 후 잔여액이 남으면 자동으로 이 분류로 노출됩니다.</span>
                </div>
              </div>
            </div>
          }
        >
          <button style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
            background: '#fff', border: '1px solid var(--border)', borderRadius: 6,
            fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer',
          }}>
            <HelpCircle size={13} /> 분류 기준
          </button>
        </Popover>
        <Text type="secondary" style={{ fontSize: 12 }}>
          가맹점은 <b>반환</b> · <b>일반</b> · <b>악성</b> 3개로 분류됩니다. 자세한 기준은 왼쪽 버튼.
        </Text>
      </div>

      <MerchantDetailModal mid={detailMid} open={!!detailMid} onClose={() => setDetailMid(null)} />
    </div>
  );
}
